// Turns a failed AI insights request into something a reader can act on.
// Never shows axios's own message ("Request failed with status code 500") or
// a bare HTTP status phrase: those describe the plumbing, not what to do.

const STATUS_PHRASES = new Set([
  'Bad Request',
  'Forbidden',
  'Internal Server Error',
  'Invalid request',
  'Method Not Allowed',
  'Not Found',
  'Unprocessable Entity',
]);

// Reasons the server sends over the channel or on a generation record that
// read as internal detail.
const REWORDED = {
  'AI provider is not configured':
    'AI insights are not available right now. Please try again later.',
};

export const ANALYSIS_ERRORS = {
  offline: "We couldn't reach Waypointr. Check your connection and try again.",
  signedOut: 'Your session has expired. Sign in again to continue.',
  forbidden: "You don't have access to AI insights for this organization.",
  quota:
    'Your workspace has used its AI insights for this month. They reset at the start of next month.',
  busy: "You're going a little fast. Wait a moment, then try again.",
  load: "We couldn't load AI insights. Refresh the page to try again.",
  generate: "We couldn't start your analysis. Please try again in a moment.",
  generateFailed:
    "We couldn't finish your analysis. Please try generating it again.",
  regenerateReply: "We couldn't regenerate that reply. Please try again.",
  briefingUpdate: "We couldn't start the briefing update. Please try again.",
  followUp: "We couldn't send your message. Please try again.",
  replyFailed: "We couldn't finish that reply. Please try again.",
  draftFailed: "We couldn't finish that draft. Please try again.",
  updates: 'We lost the connection for live updates. Please try again.',
  loadDraft: "We couldn't open that draft. Please try again.",
  publishDraft: "We couldn't publish that draft. Please try again.",
  loadVersion: "We couldn't open that version. Please try again.",
};

// A server-written sentence meant for readers, such as "There are no results
// to analyze yet." Lowercase details ("nonce is required") and status
// phrases are for developers.
function readable(detail) {
  return (
    typeof detail === 'string' &&
    /^[A-Z]/.test(detail) &&
    !STATUS_PHRASES.has(detail)
  );
}

export function friendlyReason(reason, fallback) {
  if (REWORDED[reason]) {
    return REWORDED[reason];
  }
  return readable(reason) ? reason : fallback;
}

export function requestErrorMessage(error, fallback) {
  const response = error?.response;

  if (!response) {
    return ANALYSIS_ERRORS.offline;
  }

  switch (response.status) {
    case 401:
      return ANALYSIS_ERRORS.signedOut;
    case 403:
      return ANALYSIS_ERRORS.forbidden;
    case 429:
      // The quota response names it; the request rate limiter does not.
      return /quota/i.test(response.data?.errors?.detail || '')
        ? ANALYSIS_ERRORS.quota
        : ANALYSIS_ERRORS.busy;
    case 400:
    case 422:
      return friendlyReason(
        response.data?.errors?.detail || response.data?.message,
        fallback
      );
    default:
      return fallback;
  }
}
